import { useQuery } from "@tanstack/react-query";
import type { AnalyticsQuery, AnalyticsResponse } from "@salesboard/contracts";
import { api, toQueryString } from "@/lib/api";

/**
 * The analytics read.
 *
 * The key roots at `analytics`, which `SSE_ENTITY_QUERY_ROOT` lists for `opportunity`,
 * `stage` and `setting` — so a card moved by anyone, a renamed stage and a changed stalled
 * threshold all invalidate these figures. Decision Log row 34: the strip and its Detailed view
 * are live, on the same events the board uses. Numbers that disagree with the cards on screen
 * are the exact mistrust this product exists to remove — and since the whole of analytics now
 * renders from *this one query*, the tiles and both tabs cannot even fall out of step.
 */
export function analyticsQueryKey(params: AnalyticsQuery) {
  return ["analytics", params] as const;
}

export function useAnalytics(params: AnalyticsQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: analyticsQueryKey(params),
    // The whole query object, spread rather than a hand-copied field list. An enumerated
    // list is exactly how the board's `q` reached the cache key but not the request, so the
    // board refetched *unfiltered* while its chip claimed otherwise.
    queryFn: () => api.get<AnalyticsResponse>(`/opportunities/analytics${toQueryString({ ...params })}`),
    // Held until the year window resolves: an unscoped read is every year at once, which is
    // a different data set rather than a slightly early one — and a tile is not allowed to
    // show a figure it will replace.
    enabled: options.enabled ?? true,
  });
}
